import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-carousel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './carousel.component.html',
  styleUrl: './carousel.component.scss',
})
export class CarouselComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() items: any[] = [];
  @Input() itemsToShow: number = 3; // Number of items visible at once
  @Input() autoPlay: boolean = false;
  @Input() autoPlayInterval: number = 5000;
  @Input() showDots: boolean = true;
  @Input() showArrows: boolean = true;
  @Input() gap: number = 24; // Gap between items in pixels

  @Output() slideChange = new EventEmitter<number>();

  @ViewChild('carouselTrack', { static: false }) carouselTrack!: ElementRef;

  currentIndex = 0;
  autoPlayIntervalId: any;
  isDragging = false;
  startX = 0;
  currentTranslate = 0;
  prevTranslate = 0;

  ngOnInit(): void {
    if (this.autoPlay) {
      this.startAutoPlay();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset to first slide when items change
    if (changes['items']) {
      this.currentIndex = 0;
      if (this.carouselTrack && !changes['items'].firstChange) {
        this.updateCarousel();
        this.updateTrackWidth();
      }
    }
    
    // Update carousel when itemsToShow changes
    if (changes['itemsToShow'] && !changes['itemsToShow'].firstChange) {
      this.currentIndex = 0;
      if (this.carouselTrack) {
        this.updateCarousel();
        this.updateTrackWidth();
      }
    }
  }

  ngAfterViewInit(): void {
    this.updateCarousel();
    this.updateTrackWidth();
  }

  updateTrackWidth(): void {
    if (this.carouselTrack && this.items.length > 0) {
      // Calculate total width needed for all items
      // Each slide shows itemsToShow items, so we need (items.length / itemsToShow) slides
      const totalSlides = Math.ceil(this.items.length / this.itemsToShow);
      const trackWidth = totalSlides * 100;
      this.carouselTrack.nativeElement.style.width = `${trackWidth}%`;
    }
  }

  ngOnDestroy(): void {
    this.stopAutoPlay();
  }

  get totalSlides(): number {
    const slides = Math.ceil(this.items.length / this.itemsToShow);
    return slides;
  }

  get slidesArray(): number[] {
    return Array.from({ length: this.totalSlides }, (_, i) => i);
  }

  get canGoPrev(): boolean {
    return this.currentIndex > 0;
  }

  get canGoNext(): boolean {
    return this.currentIndex < this.totalSlides - 1;
  }

  goToPrev(): void {
    if (this.canGoPrev) {
      this.currentIndex--;
      this.updateCarousel();
      this.slideChange.emit(this.currentIndex);
      this.resetAutoPlay();
    }
  }

  goToNext(): void {
    if (this.canGoNext) {
      this.currentIndex++;
      this.updateCarousel();
      this.slideChange.emit(this.currentIndex);
      this.resetAutoPlay();
    }
  }

  goToSlide(index: number): void {
    if (index >= 0 && index < this.totalSlides) {
      this.currentIndex = index;
      this.updateCarousel();
      this.slideChange.emit(this.currentIndex);
      this.resetAutoPlay();
    }
  }

  updateCarousel(): void {
    if (this.carouselTrack) {
      // Move by full viewport width (100%) per slide index
      // Each slide shows itemsToShow cards, so clicking arrow shows a new set of cards
      const translateX = -(this.currentIndex * 100);
      this.carouselTrack.nativeElement.style.transform = `translateX(${translateX}%)`;
    }
  }

  startAutoPlay(): void {
    this.autoPlayIntervalId = setInterval(() => {
      if (this.canGoNext) {
        this.goToNext();
      } else {
        this.currentIndex = 0;
        this.updateCarousel();
        this.slideChange.emit(this.currentIndex);
      }
    }, this.autoPlayInterval);
  }

  stopAutoPlay(): void {
    if (this.autoPlayIntervalId) {
      clearInterval(this.autoPlayIntervalId);
    }
  }

  resetAutoPlay(): void {
    if (this.autoPlay) {
      this.stopAutoPlay();
      this.startAutoPlay();
    }
  }

  getVisibleItems(): any[] {
    const startIndex = this.currentIndex * this.itemsToShow;
    return this.items.slice(startIndex, startIndex + this.itemsToShow);
  }

  // Touch/Drag handlers
  onTouchStart(event: TouchEvent | MouseEvent): void {
    this.isDragging = true;
    this.startX = this.getPositionX(event);
    this.prevTranslate = this.currentTranslate;
    this.stopAutoPlay();
  }

  onTouchMove(event: TouchEvent | MouseEvent): void {
    if (this.isDragging) {
      const currentX = this.getPositionX(event);
      this.currentTranslate = this.prevTranslate + currentX - this.startX;
    }
  }

  onTouchEnd(): void {
    this.isDragging = false;
    const movedBy = this.currentTranslate - this.prevTranslate;

    // If moved enough, go to next/prev slide
    if (movedBy < -50 && this.canGoNext) {
      this.goToNext();
    } else if (movedBy > 50 && this.canGoPrev) {
      this.goToPrev();
    } else {
      this.updateCarousel();
    }

    this.resetAutoPlay();
  }

  private getPositionX(event: TouchEvent | MouseEvent): number {
    return event instanceof TouchEvent ? event.touches[0].clientX : event.clientX;
  }
}



